// Documents main pane — extends AppMain. Header carries folder title +
// search; Body renders a card grid filtered by selectedFolder and query.

import { DOC_FOLDERS } from '../../state.js'

export const _ensureContent = (c) =>
  c && typeof c === 'object' ? c : { blocks: [] }

export const _navigate = (el, path) => {
  let rootEl = el
  while (rootEl && !rootEl.routes) rootEl = rootEl.parent
  const router = el.context?.utils?.router
  if (router && rootEl)
    router(path, rootEl, {}, { pushState: true, scrollToTop: true })
}

const _fileTypeIcon = (type) => {
  const map = {
    pdf: 'document',
    spreadsheet: 'chart',
    doc: 'document',
    image: 'image'
  }
  return map[type] || 'document'
}

const _fileTypeLabel = (type) => {
  const map = {
    pdf: 'PDF',
    spreadsheet: 'Spreadsheet',
    doc: 'Document',
    image: 'Image'
  }
  return map[type] || 'File'
}

const _filtered = (s) => {
  const folder = s.root.selectedFolder || 'all'
  const q = (s.root.docsQuery || '').toLowerCase().trim()
  let docs = s.root.documents || []
  if (folder !== 'all') docs = docs.filter((d) => d.folder === folder)
  if (q) {
    docs = docs.filter(
      (d) =>
        (d.title || '').toLowerCase().includes(q) ||
        (d.summary || '').toLowerCase().includes(q) ||
        (d.author || '').toLowerCase().includes(q)
    )
  }
  return docs
}

export const DocsMain = {
  extends: 'AppMain',
  padding: '0',
  Header: { extends: 'DocsHeader' },
  Body: {
    padding: 'B C',
    overflow: 'auto',
    DocsGrid: {}
  }
}

// ── Shared base header for Documents / Notes / KB views ──
// Each view extends this and overrides title, count, search placeholder,
// and primary action button. OnlineBar + AiToggle are always present.

export const DocsContentHeader = {
  extends: 'PageHeader',

  LeftGroup: {
    flow: 'y',
    gap: 'V',
    alignItems: 'flex-start',
    flex: '1',

    Title: {
      tag: 'h2',
      margin: '0',
      fontSize: 'C',
      fontWeight: '700',
      color: 'title',
      text: ''
    },
    Count: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      text: ''
    }
  },

  RightGroup: {
    // OnlineBar / AiToggle already live in the global AppHeader.
    Search: {
      tag: 'input',
      type: 'search',
      placeholder: 'Search...',
      padding: 'Z A',
      round: 'Z',
      borderWidth: '1px',
      borderColor: 'line',
      borderStyle: 'solid',
      background: 'overlay',
      color: 'title',
      fontSize: 'Z2',
      width: 'F'
    },

    PrimaryBtn: {
      tag: 'button',
      type: 'button',
      padding: 'Z A',
      round: 'Z',
      borderWidth: '1px',
      borderColor: 'line',
      borderStyle: 'solid',
      theme: 'primary',
      cursor: 'pointer',
      fontSize: 'Z2',
      fontWeight: '600',
      ':hover': { opacity: '.9' }
    }
  }
}

export const DocsHeader = {
  extends: 'DocsContentHeader',

  LeftGroup: {
    Title: {
      text: (el, s) => {
        const f = DOC_FOLDERS.find((x) => x.key === s.root.selectedFolder)
        return f?.label || 'Documents'
      }
    },
    Count: {
      text: (el, s) => {
        const n = _filtered(s).length
        return `${n} document${n === 1 ? '' : 's'}`
      }
    }
  },

  RightGroup: {
    Search: {
      placeholder: 'Search documents...',
      value: (el, s) => s.root.docsQuery || '',
      onInput: (e, el, s) => {
        s.root.update({ query: e.target.value })
      }
    },

    UploadBtn: {
      tag: 'button',
      type: 'button',
      text: 'Upload file',
      padding: 'Z A',
      round: 'Z',
      borderWidth: '1px',
      borderColor: 'line',
      borderStyle: 'solid',
      background: 'overlay',
      cursor: 'pointer',
      fontSize: 'Z2',
      fontWeight: '500',
      color: 'title',
      ':hover': { background: 'overlayStrong' },
      onClick: (e, el, s) => {
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.md,.txt,.png,.jpg'
        input.onchange = async () => {
          const file = input.files[0]
          if (!file) return
          const folder =
            s.root.selectedFolder === 'all' ? 'company' : s.root.selectedFolder
          const ext = file.name.split('.').pop().toLowerCase()
          const typeMap = {
            pdf: 'pdf',
            doc: 'doc',
            docx: 'doc',
            xls: 'spreadsheet',
            xlsx: 'spreadsheet',
            png: 'image',
            jpg: 'image',
            md: 'doc',
            txt: 'doc'
          }
          const row = {
            title: file.name.replace(/\.[^.]+$/, ''),
            folder,
            type: typeMap[ext] || 'doc',
            author: s.root.currentUserEmail || 'Unknown',
            // Populate `author_email` for Phase E ownership-gated RLS (0069).
            author_email: s.root.currentUserEmail || null,
            summary: `Uploaded file: ${file.name}`
          }
          // migrated 2026-04-27 to SDK route workspaceProject.documents.create
          let data = null
          try {
            data = await el.sdk.execute('workspaceProject.documents', 'create',
              { payload: row })
          } catch (err) {
            console.error('[Documents] Upload insert failed:', err?.message || err)
            return
          }
          if (!data?.id) {
            console.error('[Documents] Upload insert returned no id')
            return
          }
          const docs = s.root.documents || []
          s.root.update({
            documents: [
              ...docs,
              {
                id: data.id,
                ...row,
                updatedAt: new Date().toISOString().slice(0, 10),
                content: ''
              }
            ]
          })
        }
        input.click()
      }
    },

    PrimaryBtn: {
      text: '+ New document',
      onClick: (e, el, s) => {
        const folder =
          s.root.selectedFolder === 'all' ? 'company' : s.root.selectedFolder
        // Create a draft locally — don't save to DB until the user edits
        const docs = s.root.documents || []
        const draftId = 'draft-' + Date.now()
        const me = (s.root.people || []).find(p => p.email === s.root.currentUserEmail)
        const doc = {
          id: draftId,
          title: 'Untitled document',
          folder,
          type: 'doc',
          author: me?.name || s.root.currentUserEmail || 'Unknown',
          summary: '',
          content: { blocks: [] },
          isDraft: true,
          updatedAt: new Date().toISOString().slice(0, 10)
        }
        s.root.update({
          documents: [...docs, doc],
          editorDoc: { id: draftId, title: doc.title, content: { blocks: [] }, isDraft: true },
          editorMode: 'edit',
          activeSection: 'editor'
        })
        _navigate(el, '/documents/editor')
      }
    }
  }
}

export const DocsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
  gap: 'A',

  childExtends: 'DocsCard',
  childrenAs: 'state',
  children: (el, s) => _filtered(s)
}

export const DocsCard = {
  extends: 'Link',
  href: (el, s) => `/documents/editor#doc=${encodeURIComponent(s.id || '')}`,
  textDecoration: 'none',
  color: 'inherit',
  flow: 'y',
  gap: 'Z1',
  padding: 'B',
  round: 'A',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',
  background: 'overlayStrong',
  cursor: 'pointer',
  transition: 'A defaultBezier',
  ':hover': { borderColor: 'title', transform: 'translateY(-2px)' },
  onClick: (e, el, s) => {
    e.preventDefault()
    s.root.update({
      editorDoc: { id: s.id, title: s.title, content: _ensureContent(s.content) },
      editorMode: 'view',
      activeSection: 'editor'
    })
    _navigate(el, `/documents/editor#doc=${encodeURIComponent(s.id || '')}`)
  },

  Top: {
    flow: 'x',
    align: 'center left',
    gap: 'Z1',

    Icon: {
      icon: (el, s) => _fileTypeIcon(s.type),
      boxSize: 'A2 A2',
      color: 'caption'
    },
    Title: {
      tag: 'h3',
      margin: '0',
      fontSize: 'A',
      fontWeight: '600',
      color: 'title',
      flex: '1',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.title || 'Untitled'
    },
    TypeBadge: {
      tag: 'span',
      fontSize: 'Y1',
      fontWeight: '600',
      padding: 'V Z',
      round: 'Y',
      background: 'overlay',
      color: 'caption',
      textTransform: 'uppercase',
      letterSpacing: '.05em',
      text: (el, s) => _fileTypeLabel(s.type)
    }
  },

  Summary: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z2',
    lineHeight: '1.5',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: (el, s) => {
      if (s.summary) return 'caption'
      const blocks = s.content?.blocks || []
      const text = blocks.map(b => b.data?.text || '').filter(Boolean).join(' ')
      return text ? 'caption' : 'caption.5'
    },
    fontStyle: (el, s) => {
      if (s.summary) return 'normal'
      const blocks = s.content?.blocks || []
      return blocks.some(b => b.data?.text) ? 'normal' : 'italic'
    },
    text: (el, s) => {
      if (s.summary) return s.summary
      const blocks = s.content?.blocks || []
      const text = blocks.map(b => b.data?.text || '').filter(Boolean).join(' ').replace(/<[^>]+>/g, '')
      return text || 'No description'
    }
  },

  Meta: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    marginTop: 'Z',
    fontSize: 'Y2',
    color: 'caption',

    Author: {
      flow: 'x',
      align: 'center',
      gap: 'Y',
      Avatar: {
        avatarType: 'initials',
        boxSize: 'A1',
        src: (el, s) => el.call('avatarUrlFor', s.author)
      },
      AuthorName: {
        tag: 'span',
        text: (el, s) => {
          const author = s.author || ''
          if (!author.includes('@')) return author
          const person = (s.root?.people || []).find(p => p.email === author)
          return person?.name || author
        }
      }
    },
    Updated: {
      tag: 'span',
      text: (el, s) => {
        if (!s.updatedAt) return ''
        const ms = Date.now() - new Date(s.updatedAt).getTime()
        const mins = Math.floor(ms / 60000)
        if (mins < 1) return 'just now'
        if (mins < 60) return `${mins} min ago`
        const hrs = Math.floor(mins / 60)
        if (hrs < 24) return `${hrs} hour${hrs > 1 ? 's' : ''} ago`
        const days = Math.floor(hrs / 24)
        if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`
        const months = Math.floor(days / 30)
        return `${months} month${months > 1 ? 's' : ''} ago`
      }
    }
  }
}
